import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useWithdrawRequest } from '../store/slice/withdrawRequestSlice';
import { ActionButton, ActionButtonGroup } from '../components/ActionControls';

function formatCurrency(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

function statusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'pending':
      return 'status-pill-warning';
    case 'approved':
    case 'completed':
      return 'status-pill-success';
    case 'rejected':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'status-pill-muted';
  }
}

function DetailRow({ label, value, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${className}`}>
      <dt className="text-sm font-medium text-slate-500 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-800 sm:text-right break-all">{value}</dd>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="card-premium p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">{title}</h2>
      <dl className="space-y-4">{children}</dl>
    </div>
  );
}

function WithdrawRequestDetailPage() {
  const { id } = useParams();
  const {
    selectedWithdrawRequest,
    detailLoading,
    loading,
    error,
    fetchWithdrawRequestById,
    updateWithdrawRequestStatus,
    clearSelectedWithdrawRequest,
  } = useWithdrawRequest();

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (id) fetchWithdrawRequestById(id);
    return () => clearSelectedWithdrawRequest();
  }, [id, fetchWithdrawRequestById, clearSelectedWithdrawRequest]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleApprove = () => {
    if (!selectedWithdrawRequest) return;
    updateWithdrawRequestStatus({ id: selectedWithdrawRequest.id, status: 'approved' }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal request approved');
        fetchWithdrawRequestById(selectedWithdrawRequest.id);
      }
    });
  };

  const handleComplete = () => {
    if (!selectedWithdrawRequest) return;
    updateWithdrawRequestStatus({ id: selectedWithdrawRequest.id, status: 'completed' }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal marked as completed');
        fetchWithdrawRequestById(selectedWithdrawRequest.id);
      }
    });
  };

  const handleReject = () => {
    if (!selectedWithdrawRequest) return;
    updateWithdrawRequestStatus({
      id: selectedWithdrawRequest.id,
      status: 'rejected',
      admin_note: rejectReason.trim() || undefined,
    }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal request rejected');
        fetchWithdrawRequestById(selectedWithdrawRequest.id);
      }
      setRejectModalOpen(false);
      setRejectReason('');
    });
  };

  if (detailLoading && !selectedWithdrawRequest) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading withdraw request...</p>
      </div>
    );
  }

  if (!selectedWithdrawRequest) {
    return (
      <div className="space-y-4">
        <Link
          to="/withdraw-requests"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Withdraw Requests
        </Link>
        <div className="card-premium py-16 text-center text-slate-500">Withdraw request not found.</div>
      </div>
    );
  }

  const req = selectedWithdrawRequest;
  const isPending = req.status === 'pending';
  const isApproved = req.status === 'approved';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Link
          to="/withdraw-requests"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Withdraw Requests
        </Link>
        {(isPending || isApproved) && (
          <ActionButtonGroup align="left">
            {isPending && (
              <>
                <ActionButton type="button" variant="success" onClick={handleApprove} disabled={loading}>
                  Approve
                </ActionButton>
                <ActionButton type="button" variant="danger" onClick={() => setRejectModalOpen(true)}>
                  Reject
                </ActionButton>
              </>
            )}
            {isApproved && (
              <ActionButton type="button" variant="success" onClick={handleComplete} disabled={loading}>
                Mark as Completed
              </ActionButton>
            )}
          </ActionButtonGroup>
        )}
      </div>

      <div className="card-premium overflow-hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-amber-700 px-6 py-8 sm:px-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.18),_transparent_35%)]" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-amber-100/80">Withdrawal Request #{req.id}</p>
              <h1 className="mt-1 font-display text-3xl font-bold text-white tracking-tight">
                {formatCurrency(req.requested_amount)}
              </h1>
              <p className="mt-2 text-sm text-slate-200">
                {req.vendor_name || 'Unknown vendor'}
                {req.salon_name ? ` · ${req.salon_name}` : ''}
              </p>
            </div>
            <span className={`status-pill capitalize self-start sm:self-auto ${statusColor(req.status)}`}>
              {req.status || 'pending'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-100">
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">Requested Amount</p>
            <p className="mt-1 text-2xl font-bold text-slate-800">{formatCurrency(req.requested_amount)}</p>
          </div>
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">
              Deduction{req.deduction_percentage != null ? ` (${req.deduction_percentage}%)` : ''}
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-600">{formatCurrency(req.deduction_amount)}</p>
          </div>
          <div className="bg-white p-5">
            <p className="text-sm font-medium text-slate-500">Net Amount</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{formatCurrency(req.net_amount)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DetailSection title="Request Details">
          <DetailRow label="Request ID" value={formatValue(req.id)} />
          <DetailRow label="Status" value={<span className="capitalize">{formatValue(req.status)}</span>} />
          <DetailRow
            label="Created At"
            value={req.created_at ? moment(req.created_at).format('DD/MM/YYYY HH:mm') : '—'}
          />
          <DetailRow
            label="Updated At"
            value={req.updated_at ? moment(req.updated_at).format('DD/MM/YYYY HH:mm') : '—'}
          />
          <DetailRow label="Admin Note" value={formatValue(req.admin_note)} />
        </DetailSection>

        <DetailSection title="Vendor Details">
          <DetailRow label="Vendor Name" value={formatValue(req.vendor_name)} />
          <DetailRow label="Email" value={formatValue(req.vendor_email)} />
          <DetailRow label="Phone" value={formatValue(req.vendor_phone)} />
          <DetailRow label="User ID" value={formatValue(req.user_id)} />
        </DetailSection>

        <DetailSection title="Salon Details">
          <DetailRow label="Salon Name" value={formatValue(req.salon_name)} />
          <DetailRow label="Salon ID" value={formatValue(req.salon_id)} />
        </DetailSection>

        <DetailSection title="Bank Account Details">
          <DetailRow label="Account Holder" value={formatValue(req.account_holder_name)} />
          <DetailRow label="Bank Name" value={formatValue(req.bank_name)} />
          <DetailRow label="Account Number" value={formatValue(req.account_number)} />
          <DetailRow label="IFSC Code" value={formatValue(req.ifsc_code)} />
          <DetailRow label="Account Type" value={<span className="capitalize">{formatValue(req.account_type)}</span>} />
          <DetailRow label="Branch Name" value={formatValue(req.branch_name)} />
        </DetailSection>
      </div>

      {rejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRejectModalOpen(false)}>
          <div className="card-premium p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-2">Reject Withdraw Request</h3>
            <p className="text-sm text-slate-600 mb-4">
              Rejecting request from <strong>{req.vendor_name}</strong> for {formatCurrency(req.requested_amount)}.
            </p>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin note (optional)</label>
            <textarea
              placeholder="Provide a reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows="3"
              className="input-premium resize-none mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectReason('');
                }}
                className="btn-premium bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button onClick={handleReject} className="btn-primary" disabled={loading}>
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WithdrawRequestDetailPage;
